import { useMemo } from 'react';
import { InsightsLineChart } from '@repo/ui/components/cards/InsightsLineChart';
import { MetricConfig, CHART_PADDING } from '../config/chartConfig';
import { STAT_VALUE_PRECISION } from '../config/constants';

interface ChartSeries {
  id: string;
  color: string;
  data: { x: number; y: number }[];
}

interface SingleChartProps {
  series: ChartSeries;
  config: MetricConfig;
  height: number;
  timeRange: { start: number; end: number } | null;
  isLast: boolean;
}

const STAT_HEADER_PX = 28;

function calcValueRange(series: ChartSeries) {
  const all = series.data.map(p => p.y);
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min;
  const paddingPct =
    range < 1 ? CHART_PADDING.SMALL_RANGE : range < 10 ? CHART_PADDING.MEDIUM_RANGE : CHART_PADDING.LARGE_RANGE;
  const padding = Math.max(range * paddingPct, CHART_PADDING.MIN_VALUE);
  return {
    min: Math.floor((min - padding) * 100) / 100,
    max: Math.ceil((max + padding) * 100) / 100,
  };
}

function calcStats(data: { y: number }[]) {
  const ys = data.map(p => p.y);
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const avg = ys.reduce((a, b) => a + b, 0) / ys.length;
  return { min, avg, max };
}

export const SingleChart = ({ series, config, height, timeRange, isLast }: SingleChartProps) => {
  const valueRange = useMemo(() => calcValueRange(series), [series]);
  const stats = useMemo(() => calcStats(series.data), [series]);

  const nivoData = useMemo(
    () => [{ id: series.id, color: series.color, data: series.data }],
    [series],
  );

  return (
    <div style={{ height }}>
      <div
        style={{ height: STAT_HEADER_PX }}
        className="flex items-center gap-2 overflow-hidden border-b border-[var(--anl-border)] px-3 text-xs text-[var(--anl-text-muted)]"
      >
        <span className="shrink-0" style={{ color: config.color }}>●</span>
        <span className="shrink-0 whitespace-nowrap font-medium text-[var(--anl-text)]">{config.label}</span>
        <span className="shrink-0 whitespace-nowrap">
          Min <b style={{ color: config.color }}>{stats.min.toFixed(STAT_VALUE_PRECISION)}</b>
        </span>
        <span className="shrink-0 whitespace-nowrap">
          Avg <b style={{ color: config.color }}>{stats.avg.toFixed(STAT_VALUE_PRECISION)}</b>
        </span>
        <span className="shrink-0 whitespace-nowrap">
          Max <b style={{ color: config.color }}>{stats.max.toFixed(STAT_VALUE_PRECISION)}</b>
        </span>
        {config.unit && <span className="truncate text-[var(--anl-text-muted)]">{config.unit}</span>}
      </div>
      <InsightsLineChart
        data={nivoData}
        height={height - STAT_HEADER_PX}
        valueRange={valueRange}
        timeRange={timeRange ?? undefined}
        showXAxis={isLast}
        seriesColor={series.color}
        tooltipLabel={config.label}
        tooltipUnit={config.unit || undefined}
      />
    </div>
  );
};
